
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from './spinner';

type BtnVariant =
  | 'default'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'dark'
  | 'destructive'
  | 'destructiveSoft'
  | 'info'
  | 'ai'
  | 'link'
  | 'inverse'
  | 'whatsapp'
  | 'whatsappOutline'
  | 'unstyled'
  | 'raised';
type BtnSize = 'sm' | 'md' | 'lg' | 'icon' | 'iconSm';

const BTN_VARIANT: Record<BtnVariant, string> = {
  default: 'bg-medlux text-medlux-foreground hover:bg-primary-hover',
  // The primary action on a picture or glass (the sign-in, owner 2026-11-03): the flower's leaf
  // green with a glossy top light and a soft lower shade, no drop shadow.
  raised:
    'bg-brand-green bg-[linear-gradient(180deg,color-mix(in_srgb,var(--brand-green),white_12%),color-mix(in_srgb,var(--brand-green),black_14%))] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.4),inset_0_-2px_6px_rgba(0,0,0,0.12)] hover:brightness-110 active:translate-y-px',
  outline: 'border border-border bg-white text-foreground hover:bg-muted',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-muted',
  ghost: 'text-foreground hover:bg-muted',
  dark: 'bg-medlux-strong text-medlux-foreground hover:brightness-110',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive-hover',
  // A destructive action that is not the screen's main one (ds "Cancelar sessão").
  destructiveSoft:
    'bg-destructive-soft text-destructive-soft-foreground hover:bg-destructive-soft-foreground/15',
  // AI actions (ds "Revisar com IA"): blue, so a model's step never reads as the primary green.
  info: 'bg-info-subtle text-info hover:bg-info/15',
  // The one AI action of a surface, set apart by a slow light sweep (globals .ai-glow; still
  // under reduced motion).
  ai: 'ai-glow text-info hover:brightness-95',
  // A white pill on a coloured band (the campaign strip): the band sets `--inverse-fg`
  // to its own colour and the pill's text reads it, so one variant serves every tone.
  inverse: 'bg-white text-[var(--inverse-fg,var(--foreground))] shadow-xs hover:bg-white/90',
  // Every "Falar no WhatsApp" action: the one WhatsApp green, on an AA token (globals.css).
  whatsapp: 'bg-whatsapp text-whatsapp-foreground hover:bg-whatsapp-hover',
  // The same WhatsApp green one step lighter (green on white, 5.0:1), for a card where the green
  // CTA is the primary action and WhatsApp the second one.
  whatsappOutline: 'border-2 border-whatsapp bg-white text-whatsapp hover:bg-whatsapp/5',
  // Text link — for inline mode-switch actions. Skips the sized pill (see below).
  // Wraps like text (a long link must never widen its container: it pushed a 375px page to
  // 382px once).
  link: 'text-medlux font-medium underline-offset-4 hover:underline whitespace-normal text-left',
  // Interaction-only escape hatch for highly bespoke controls (calendar cells,
  // star pickers, media thumbnails). They still get one button implementation,
  // default type, disabled behavior, and keyboard focus treatment.
  unstyled: '',
};

const BTN_SIZE: Record<BtnSize, string> = {
  sm: 'h-8 px-3 text-[0.8125rem] gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-11 px-6 text-[0.9375rem] gap-2',
  icon: 'h-11 w-11',
  iconSm: 'h-7 w-7',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  block?: boolean;
  /** Show a spinner and disable the button (replaces hand-rolled inline spinners). */
  loading?: boolean;
  asChild?: boolean;
}

export function Button({
  variant = 'default',
  size = 'md',
  block = false,
  loading = false,
  disabled,
  className,
  type = 'button',
  asChild = false,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(
    // Focus: a solid navy ring on a white offset (3:1 on any ground); a coloured band can
    // hand its own ring colour down through --btn-ring (the red strip uses white).
    'disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--btn-ring,var(--color-medlux))] focus-visible:ring-offset-2',
    variant !== 'unstyled' &&
      // Approved action radius; chips keep their own pill shape.
      // Press feedback on pointer-down (Apple/Emil): a 150ms spring-shaped return, no
      // movement under reduced motion, and no 300ms tap delay on touch.
      'inline-flex items-center justify-center whitespace-nowrap rounded-control font-semibold touch-manipulation transition-[color,background-color,border-color,scale,filter] duration-150 ease-spring active:scale-[0.98] motion-reduce:scale-none',
    BTN_VARIANT[variant],
    variant !== 'link' && variant !== 'unstyled' && BTN_SIZE[size], // link/unstyled skip the sized pill
    block && 'w-full',
    className,
  );
  // asChild: render the single child (e.g. a <Link>) styled as the button, so
  // <Button asChild><Link/></Button> is ONE <a> — no invalid <a><button> nesting.
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ className?: string }>;
    return React.cloneElement(child, { className: cn(classes, child.props.className), ...props });
  }
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...props}
    >
      {loading && <Spinner size={size === 'sm' ? 14 : 16} className="-ml-0.5" />}
      {children}
    </button>
  );
}
